import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import useDialog from 'global/hooks/useDialog'
import { t } from 'i18next'
import { type ReactElement, type ReactNode, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import type { Contact } from './CarrierProfile.types'
import CreateOrEditContactDialog from './CreateOrEditContactDialog'

export interface LaneOrRegionContactsProps {
  contacts: Contact[]
}

const LaneOrRegionDialogContacts = ({
  contacts,
}: LaneOrRegionContactsProps): ReactNode => {
  const [options, setOptions] = useState<Contact[]>(contacts)
  const { watch, getValues, setValue } = useFormContext()
  const watchedContactIds: string[] = watch('contactIds')

  const handleCreateOrEditContact = useCallback((contact: Contact) => {
    setOptions([contact, ...options])
    setValue('contactIds', [...getValues('contactIds'), contact.id])
  }, [options])

  const createOrEditContactDialog = useDialog<typeof CreateOrEditContactDialog>({
    onAction: handleCreateOrEditContact,
  })

  return (
    <>
      <Stack direction="column" spacing={1} alignItems="start">
        <Autocomplete
          multiple
          options={options}
          value={options.filter((contact) => watchedContactIds.includes(contact.id))}
          onChange={(_event, newValue): void => {
            setValue('contactIds', newValue.map((contact) => contact.id))
          }}
          getOptionLabel={(option) => option.name}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...itemProps } = getItemProps({ index })
              return (
                <Chip
                  key={key}
                  {...itemProps}
                  variant="outlined"
                  size="small"
                  label={option.name}
                />
              )
            })
          }
          renderInput={(params): ReactElement => (
            <TextField {...params} label={t('screens.cs.carrier_profile.lanes_and_regions.form.contacts')} />
          )}
          fullWidth
        />
        <Button
          onClick={createOrEditContactDialog.handleOpen}
          variant="contained"
          color="secondary"
          size="small"
        >
          {t('screens.cs.carrier_profile.lanes_and_regions.form.new_contact')}
        </Button>
      </Stack>
      <CreateOrEditContactDialog
        open={createOrEditContactDialog.isOpen}
        onAction={createOrEditContactDialog.handleAction}
        onClose={createOrEditContactDialog.handleClose}
      />
    </>
  )
}

export default LaneOrRegionDialogContacts
